import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit, X, ArrowUp, ArrowDown } from "lucide-react";

const empty = {
  headline: "", subtext: "", cta_label: "SHOP NOW", cta_href: "/catalogue",
  image_url: "", badge: "", order: 0, enabled: true, theme: "emerald",
};

export default function AdminBanners() {
  const [rows, setRows] = useState([]);
  const [autoplayMs, setAutoplayMs] = useState(5000);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/admin/banners").then((r) => setRows(r.data));
  const loadSettings = () => api.get("/settings").then((r) => setAutoplayMs(r.data?.carousel_autoplay_ms || 5000));
  useEffect(() => { load(); loadSettings(); }, []);

  const open = (b) => { setEditing(b); setForm(b ? { ...empty, ...b } : { ...empty, order: rows.length + 1 }); };
  const close = () => { setEditing(null); setForm(empty); };

  const save = async () => {
    try {
      const payload = { ...form, order: Number(form.order || 0) };
      if (editing?.id) await api.put(`/admin/banners/${editing.id}`, payload);
      else await api.post("/admin/banners", payload);
      toast.success("Saved"); close(); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete this banner?")) return;
    await api.delete(`/admin/banners/${id}`); toast.success("Deleted"); load(); };
  const toggle = async (b) => {
    await api.put(`/admin/banners/${b.id}`, { ...b, enabled: !b.enabled });
    load();
  };
  const reorder = async (b, dir) => {
    const sorted = [...rows].sort((a, c) => a.order - c.order);
    const i = sorted.findIndex((x) => x.id === b.id);
    const j = i + dir;
    if (j < 0 || j >= sorted.length) return;
    const swap = sorted[j];
    await Promise.all([
      api.put(`/admin/banners/${b.id}`, { ...b, order: swap.order }),
      api.put(`/admin/banners/${swap.id}`, { ...swap, order: b.order }),
    ]);
    load();
  };
  const saveTiming = async () => {
    await api.put("/admin/settings", { carousel_autoplay_ms: Number(autoplayMs) });
    toast.success("Auto-slide timing updated");
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h1 className="font-display text-3xl font-bold">Promotional banners</h1>
        <button onClick={() => open(null)} className="btn-primary" data-testid="admin-add-banner"><Plus className="h-4 w-4"/> Add banner</button>
      </div>

      <div className="card-flat p-4 mb-4 flex flex-wrap gap-3 items-end">
        <label className="block">
          <span className="text-xs font-semibold text-slate-600">Auto-slide (ms)</span>
          <input type="number" min="2000" step="500" value={autoplayMs} onChange={(e) => setAutoplayMs(e.target.value)} className="mt-1 h-10 rounded-lg border border-slate-200 px-3 text-sm w-32" data-testid="autoplay-ms"/>
        </label>
        <button onClick={saveTiming} className="btn-dark" data-testid="save-timing">Save timing</button>
        <div className="text-xs text-slate-500 ml-auto">Applies to the homepage carousel for all visitors.</div>
      </div>

      <div className="card-flat overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left p-3 w-14">Order</th>
              <th className="text-left p-3">Preview</th>
              <th className="text-left p-3">Headline</th>
              <th className="text-left p-3">Link</th>
              <th className="text-left p-3">Theme</th>
              <th className="text-left p-3">Enabled</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {[...rows].sort((a,b) => a.order - b.order).map((b) => (
              <tr key={b.id} className="border-t border-slate-100" data-testid={`banner-row-${b.id}`}>
                <td className="p-3">
                  <div className="flex flex-col gap-1">
                    <button onClick={() => reorder(b, -1)} className="text-slate-400 hover:text-slate-900"><ArrowUp className="h-4 w-4"/></button>
                    <span className="font-mono text-xs">{b.order}</span>
                    <button onClick={() => reorder(b, +1)} className="text-slate-400 hover:text-slate-900"><ArrowDown className="h-4 w-4"/></button>
                  </div>
                </td>
                <td className="p-3">
                  <div className="w-24 h-14 rounded-lg overflow-hidden bg-slate-100">
                    {b.image_url && <img src={b.image_url} alt="" className="w-full h-full object-cover"/>}
                  </div>
                </td>
                <td className="p-3"><div className="font-semibold">{b.headline}</div><div className="text-xs text-slate-500 truncate max-w-xs">{b.subtext}</div></td>
                <td className="p-3 font-mono text-xs">{b.cta_label} → {b.cta_href}</td>
                <td className="p-3 text-xs uppercase">{b.theme}</td>
                <td className="p-3">
                  <button onClick={() => toggle(b)} className={`text-xs px-2 py-1 rounded-full ${b.enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid={`toggle-${b.id}`}>
                    {b.enabled ? "Enabled" : "Disabled"}
                  </button>
                </td>
                <td className="p-3 text-right space-x-2">
                  <button onClick={() => open(b)} className="text-slate-700"><Edit className="h-4 w-4 inline"/></button>
                  <button onClick={() => del(b.id)} className="text-red-600"><Trash2 className="h-4 w-4 inline"/></button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan="7" className="p-10 text-center text-slate-500">No banners yet</td></tr>}
          </tbody>
        </table>
      </div>

      {editing !== null && (
        <div className="fixed inset-0 bg-slate-900/50 z-50 grid place-items-center p-4" onClick={close}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-200 flex justify-between items-center">
              <h2 className="font-display font-bold text-lg">{editing?.id ? "Edit banner" : "New banner"}</h2>
              <button onClick={close} className="p-2"><X className="h-5 w-5"/></button>
            </div>
            <div className="p-5 grid sm:grid-cols-2 gap-3">
              <label className="block sm:col-span-2"><span className="text-xs font-semibold">Headline</span>
                <input value={form.headline} onChange={(e) => setForm({...form, headline: e.target.value})} className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm" data-testid="bf-headline"/>
              </label>
              <label className="block sm:col-span-2"><span className="text-xs font-semibold">Supporting text</span>
                <textarea value={form.subtext || ""} onChange={(e) => setForm({...form, subtext: e.target.value})} rows={2} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"/>
              </label>
              <label className="block"><span className="text-xs font-semibold">Badge (small pill above headline)</span>
                <input value={form.badge || ""} onChange={(e) => setForm({...form, badge: e.target.value})} className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm"/>
              </label>
              <label className="block"><span className="text-xs font-semibold">Order</span>
                <input type="number" value={form.order} onChange={(e) => setForm({...form, order: e.target.value})} className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm"/>
              </label>
              <label className="block"><span className="text-xs font-semibold">CTA label</span>
                <input value={form.cta_label || ""} onChange={(e) => setForm({...form, cta_label: e.target.value})} className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm"/>
              </label>
              <label className="block"><span className="text-xs font-semibold">CTA destination (path)</span>
                <input value={form.cta_href || ""} onChange={(e) => setForm({...form, cta_href: e.target.value})} placeholder="/catalogue" className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm"/>
              </label>
              <label className="block sm:col-span-2"><span className="text-xs font-semibold">Image URL</span>
                <input value={form.image_url || ""} onChange={(e) => setForm({...form, image_url: e.target.value})} className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm" data-testid="bf-image"/>
                {form.image_url && <img src={form.image_url} alt="" className="mt-2 h-24 rounded-lg object-cover"/>}
              </label>
              <label className="block"><span className="text-xs font-semibold">Theme</span>
                <select value={form.theme} onChange={(e) => setForm({...form, theme: e.target.value})} className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm bg-white">
                  <option value="emerald">Emerald</option>
                  <option value="slate">Slate</option>
                  <option value="amber">Amber</option>
                </select>
              </label>
              <label className="flex items-center gap-2 text-sm mt-6">
                <input type="checkbox" checked={!!form.enabled} onChange={(e) => setForm({...form, enabled: e.target.checked})}/> Enabled
              </label>
            </div>
            <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={close} className="btn-ghost">Cancel</button>
              <button onClick={save} className="btn-primary" data-testid="bf-save">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
